// The suite's apps — names, tile colours, icons and URLs — for the shared top
// bar's launcher and brand. URLs come from each SPA's VITE_*_WEB_URL env vars
// (dev defaults below), so the waffle deep-links to the right host everywhere.

import {
    FileText,
    FileType2,
    HardDrive,
    KeyRound,
    Mail,
    MessagesSquare,
    Presentation,
    Sheet,
    ShieldCheck,
    type LucideIcon
} from "@fg/icons";
import type { SuiteAppKey } from "./appearance";
import { WORK_ADMIN_URL } from "./product-names";

export type SuiteAppId = SuiteAppKey | "account" | "admin";

export interface SuiteAppInfo {
    id: SuiteAppId;
    name: string;
    blurb: string;
    /** Tile colour — the app's identity; the top bar itself follows the user's accent. */
    color: string;
    icon: LucideIcon;
    envKey: string;
    devUrl: string;
    /** The production address when the build sets no env var: the one copy of it, for every app. */
    prodUrl?: string;
}

export const SUITE_APPS: readonly SuiteAppInfo[] = [
    { id: "mail", name: "Mail", blurb: "Inbox, calendar, tasks", color: "#0078d4", icon: Mail, envKey: "VITE_MAIL_WEB_URL", devUrl: "http://localhost:5180" },
    { id: "drive", name: "Drive", blurb: "Files, folders, sharing", color: "#0f7b6c", icon: HardDrive, envKey: "VITE_DRIVE_WEB_URL", devUrl: "http://localhost:5240" },
    { id: "docs", name: "Notes", blurb: "Documents and notes", color: "#185abd", icon: FileText, envKey: "VITE_DOCS_WEB_URL", devUrl: "http://localhost:5210" },
    { id: "sheets", name: "Workbook", blurb: "Spreadsheets and data", color: "#107c41", icon: Sheet, envKey: "VITE_SHEET_WEB_URL", devUrl: "http://localhost:5220" },
    { id: "slides", name: "Present", blurb: "Slides and decks", color: "#c43e1c", icon: Presentation, envKey: "VITE_PRESENT_WEB_URL", devUrl: "http://localhost:5232" },
    { id: "pdf", name: "PDF", blurb: "View, annotate, sign, edit", color: "#c42b1c", icon: FileType2, envKey: "VITE_PDF_WEB_URL", devUrl: "http://localhost:5250" },
    { id: "connect", name: "Meeting", blurb: "Chat, channels, meetings", color: "#5b5fc7", icon: MessagesSquare, envKey: "VITE_CONNECT_WEB_URL", devUrl: "http://localhost:5200" },
    { id: "account", name: "Account", blurb: "Sign-in and security", color: "#107c10", icon: KeyRound, envKey: "VITE_IDENTITY_WEB_URL", devUrl: "http://localhost:5002" },
    { id: "admin", name: "Admin", blurb: "Users, domains, policies", color: "#3b4a5c", icon: ShieldCheck, envKey: "VITE_ADMIN_WEB_URL", devUrl: "http://localhost:5190", prodUrl: WORK_ADMIN_URL }
];

export function suiteApp(id: SuiteAppId): SuiteAppInfo {
    return SUITE_APPS.find((a) => a.id === id) ?? (SUITE_APPS[0] as SuiteAppInfo);
}

export function suiteAppUrl(id: SuiteAppId): string {
    const app = suiteApp(id);
    const env = (import.meta as unknown as { env?: Record<string, string | boolean | undefined> }).env ?? {};
    const raw = env[app.envKey];
    if (typeof raw === "string" && raw.trim().length > 0) return raw;
    return env.PROD === true && app.prodUrl ? app.prodUrl : app.devUrl;
}
